import { useMutation, useQueryClient } from 'react-query'
import { agentService } from '../services/api'
import { showErrorAlert, showSuccessAlert, logError } from '../utils/errors'
import { useInvalidateAgents } from './useAgents'

/**
 * * Hook for agent cleanup mutations
 */
export function useAgentCleanupMutations() {
  const queryClient = useQueryClient()
  const { invalidateActiveAgents } = useInvalidateAgents()

  const cleanInactiveMutation = useMutation(
    agentService.cleanInactiveAgents,
    {
      onSuccess: (data) => {
        invalidateActiveAgents()
        queryClient.invalidateQueries('agents')
        showSuccessAlert(`${data.deletedCount} agent(s) inactif(s) supprimé(s) avec succès`)
      },
      onError: (error) => {
        logError('Nettoyage agents inactifs', error)
        showErrorAlert(error, 'Erreur lors de la suppression des agents inactifs')
      }
    }
  )

  const cleanDuplicateMutation = useMutation(
    agentService.cleanDuplicateAgents,
    {
      onSuccess: (data) => {
        invalidateActiveAgents()
        showSuccessAlert(`${data.deletedCount} agent(s) en double supprimé(s) avec succès`)
      },
      onError: (error) => {
        logError('Nettoyage agents en double', error)
        showErrorAlert(error, 'Erreur lors de la suppression des agents en double')
      }
    }
  )

  const cleanAllAgentInstancesMutation = useMutation(
    agentService.cleanAllAgentInstances,
    {
      onSuccess: (data) => {
        invalidateActiveAgents()
        queryClient.invalidateQueries('agents')
        showSuccessAlert(`${data.deletedCount} instance(s) d'agent(s) supprimée(s) avec succès`)
      },
      onError: (error) => {
        logError('Suppression toutes les instances', error)
        showErrorAlert(error, 'Erreur lors de la suppression des instances')
      }
    }
  )

  const cleanAllAgentModelsMutation = useMutation(
    agentService.cleanAllAgentModels,
    {
      onSuccess: (data) => {
        queryClient.invalidateQueries('agents')
        showSuccessAlert(`${data.deletedCount} modèle(s) d'agent(s) supprimé(s) avec succès`)
      },
      onError: (error) => {
        logError('Suppression tous les modèles', error)
        showErrorAlert(error, 'Erreur lors de la suppression des modèles')
      }
    }
  )

  const cleanAllAgentsMutation = useMutation(
    agentService.cleanAllAgents,
    {
      onSuccess: (data) => {
        invalidateActiveAgents()
        queryClient.invalidateQueries('agents')
        showSuccessAlert(`${data.deletedCount} agent(s) supprimé(s) avec succès`)
      },
      onError: (error) => {
        logError('Suppression tous les agents', error)
        showErrorAlert(error, 'Erreur lors de la suppression des agents')
      }
    }
  )

  return {
    cleanInactive: cleanInactiveMutation,
    cleanDuplicates: cleanDuplicateMutation,
    cleanAllInstances: cleanAllAgentInstancesMutation,
    cleanAllModels: cleanAllAgentModelsMutation,
    cleanAll: cleanAllAgentsMutation
  }
}

/**
 * * Hook for agent CRUD mutations
 */
export function useAgentMutations() {
  const queryClient = useQueryClient()
  const { invalidateAll } = useInvalidateAgents()

  const createMutation = useMutation(
    agentService.createAgent,
    {
      onSuccess: () => {
        invalidateAll()
      },
      onError: (error) => {
        logError('Création agent', error)
        showErrorAlert(error, 'Erreur lors de la création de l\'agent')
      }
    }
  )

  const updateMutation = useMutation(
    ({ id, data }: { id: string; data: Partial<typeof agentService.createAgent extends (arg: infer T) => any ? T : never> }) =>
      agentService.updateAgent(id, data),
    {
      onSuccess: () => {
        invalidateAll()
      },
      onError: (error) => {
        logError('Mise à jour agent', error)
        showErrorAlert(error, 'Erreur lors de la mise à jour de l\'agent')
      }
    }
  )

  const deleteMutation = useMutation(
    agentService.deleteAgent,
    {
      onSuccess: () => {
        invalidateAll()
      },
      onError: (error) => {
        logError('Suppression agent', error)
        showErrorAlert(error, 'Erreur lors de la suppression de l\'agent')
      }
    }
  )

  return {
    create: createMutation,
    update: updateMutation,
    delete: deleteMutation
  }
}
